import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Search, CalendarIcon, Package, Plus, Pencil, Trash2, ArrowRight, RotateCcw, X } from "lucide-react";
import { format } from "date-fns";
import { formatQuantityWithUnit } from "@/lib/unitPluralize";
import { cn } from "@/lib/utils";
import { DateRange } from "react-day-picker";

interface StockLogEntry {
  id: string;
  user_name: string | null;
  action_type: string;
  item_name: string;
  batch_number: string | null;
  quantity: number | null;
  event_name: string | null;
  details: string | null;
  created_at: string;
}

interface StockLogTabProps {
  ptaId: string | null;
}

const ACTION_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  item_added: { label: "Item Added", color: "bg-green-100 text-green-800", icon: <Plus className="h-3 w-3" /> },
  item_updated: { label: "Item Updated", color: "bg-blue-100 text-blue-800", icon: <Pencil className="h-3 w-3" /> },
  item_deleted: { label: "Item Deleted", color: "bg-red-100 text-red-800", icon: <Trash2 className="h-3 w-3" /> },
  batch_added: { label: "Batch Added", color: "bg-green-100 text-green-800", icon: <Plus className="h-3 w-3" /> },
  batch_updated: { label: "Batch Updated", color: "bg-blue-100 text-blue-800", icon: <Pencil className="h-3 w-3" /> },
  batch_deleted: { label: "Batch Deleted", color: "bg-red-100 text-red-800", icon: <Trash2 className="h-3 w-3" /> },
  assigned_to_event: { label: "Assigned to Event", color: "bg-purple-100 text-purple-800", icon: <ArrowRight className="h-3 w-3" /> },
  returned_from_event: { label: "Returned from Event", color: "bg-orange-100 text-orange-800", icon: <RotateCcw className="h-3 w-3" /> },
};

export function StockLogTab({ ptaId }: StockLogTabProps) {
  const [logs, setLogs] = useState<StockLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const fetchLogs = useCallback(async () => {
    if (!ptaId) return;
    setLoading(true);

    let query = supabase
      .from('catering_stock_log')
      .select('*')
      .eq('pta_id', ptaId)
      .order('created_at', { ascending: false })
      .limit(500);

    if (dateRange?.from) {
      query = query.gte('created_at', `${format(dateRange.from, 'yyyy-MM-dd')}T00:00:00`);
    }
    if (dateRange?.to) {
      query = query.lte('created_at', `${format(dateRange.to, 'yyyy-MM-dd')}T23:59:59`);
    }

    const { data, error } = await query;

    if (!error && data) {
      setLogs(data);
    }
    setLoading(false);
  }, [ptaId, dateRange]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredLogs = logs.filter(log => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.item_name?.toLowerCase().includes(query) ||
      log.user_name?.toLowerCase().includes(query) ||
      log.event_name?.toLowerCase().includes(query) ||
      log.batch_number?.toLowerCase().includes(query) ||
      log.details?.toLowerCase().includes(query) ||
      ACTION_LABELS[log.action_type]?.label.toLowerCase().includes(query)
    );
  });

  const getActionDisplay = (actionType: string) => {
    const action = ACTION_LABELS[actionType] || { 
      label: actionType.replace(/_/g, ' '), 
      color: "bg-gray-100 text-gray-800",
      icon: <Package className="h-3 w-3" />
    };
    return action;
  };

  const formatLogDetails = (log: StockLogEntry) => {
    const parts: string[] = [];
    
    // Parse details JSON
    let detailsObj: Record<string, any> = {};
    if (log.details) {
      try {
        detailsObj = JSON.parse(log.details);
      } catch {
        // Not JSON, use as plain text
        parts.push(log.details);
      }
    }
    
    const unitType = detailsObj.unit_type || "units";
    
    // Batch number
    if (log.batch_number) {
      parts.push(`Batch: ${log.batch_number}`);
    }
    
    // Quantity
    if (log.quantity !== null) {
      parts.push(`Qty: ${formatQuantityWithUnit(log.quantity, unitType)}`);
    }
    
    // Event name
    if (log.event_name) {
      parts.push(`Event: ${log.event_name}`);
    }
    
    // Supplier
    if (detailsObj.supplier) {
      parts.push(`Supplier: ${detailsObj.supplier}`);
    }
    
    // Low stock threshold
    if (detailsObj.low_stock_threshold !== undefined) {
      parts.push(`Min Stock: ${formatQuantityWithUnit(detailsObj.low_stock_threshold, unitType)}`);
    }
    
    // Expiry date
    if (detailsObj.expiry_date) {
      try {
        parts.push(`Expires: ${format(new Date(detailsObj.expiry_date), "dd MMM yyyy")}`);
      } catch {
        parts.push(`Expires: ${detailsObj.expiry_date}`);
      }
    }
    
    // Category
    if (detailsObj.category) {
      parts.push(`Category: ${detailsObj.category}`);
    }
    
    return parts.join(' • ') || '—';
  };

  const clearDateRange = () => {
    setDateRange(undefined);
  };

  return (
    <Card className="shadow-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Stock Activity Log
        </CardTitle>
        <CardDescription>
          View all stock management activity including assignments and returns
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Label htmlFor="log-search" className="sr-only">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="log-search"
                placeholder="Search by item, user, event, batch..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[280px] justify-start text-left font-normal",
                    !dateRange && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "dd MMM yyyy")} - {format(dateRange.to, "dd MMM yyyy")}
                      </>
                    ) : (
                      format(dateRange.from, "dd MMM yyyy")
                    )
                  ) : (
                    <span>Filter by date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            {dateRange && (
              <Button variant="ghost" size="icon" onClick={clearDateRange} title="Clear date filter">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Log Table */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading activity log...</div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {logs.length === 0 
              ? "No activity recorded yet" 
              : "No results match your search"}
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Date & Time</TableHead>
                  <TableHead className="w-[150px]">Action</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="w-[150px]">User</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => {
                  const action = getActionDisplay(log.action_type);
                  return (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(log.created_at), "dd MMM yyyy")}
                        <br />
                        <span className="text-xs">{format(new Date(log.created_at), "HH:mm")}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${action.color} flex items-center gap-1 w-fit`}>
                          {action.icon}
                          {action.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{log.item_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatLogDetails(log)}
                      </TableCell>
                      <TableCell className="text-sm">{log.user_name || "System"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="text-sm text-muted-foreground text-right">
          Showing {filteredLogs.length} of {logs.length} entries
        </div>
      </CardContent>
    </Card>
  );
}
